import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc, setDoc } from '../firebaseConfig';
import { Bar } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from "components/MDTypography";
import { MenuItem, Select, FormControlLabel, Switch } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const TagPerformanceChart = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showMissedTrades, setShowMissedTrades] = useState(false);
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID

  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

     // Fetch account name on mount
     useEffect(() => {
      const fetchAccountName = async () => {
        if (tradeAccountID === 'default') {
          setAccountName('Default');
          return;
        }
        try {
          const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
          if (accountDoc.exists()) {
            setAccountName(accountDoc.data().name || tradeAccountID);
          }
        } catch (error) {
          console.error('Error fetching account name:', error);
        }
      };
      fetchAccountName();
    }, [userID, tradeAccountID]); 

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefRef = doc(db, `Users/${userID}/preferences/chartSettings`);
        const prefDoc = await getDoc(prefRef);
        if (prefDoc.exists()) {
          const prefs = prefDoc.data();
          setShowMissedTrades(prefs.showMissedTradesInTagChart || false);
          setYear(prefs.tagChartYear || new Date().getFullYear());
        }
      } catch (error) {
        console.error('Error loading chart preferences:', error);
      }
    };
    loadPreferences();
  }, [userID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', showMissedTrades ? 'Missed' : 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for TagPerformanceChart:', error);
      }
    };
    fetchTrades();
  }, [userID, tradeAccountID, showMissedTrades]);

  const calculateTagData = () => {
    const tagReturns = {};
    trades
      .filter(trade => new Date(trade.exitDate).getFullYear() === year)
      .forEach(trade => {
        const tags = Array.isArray(trade.tags) ? trade.tags : [];
        tags.forEach(tag => {
          tagReturns[tag] = (tagReturns[tag] || 0) + parseFloat(trade.returns || 0);
        });
      });

    const tags = Object.keys(tagReturns);
    const barColors = tags.map(tag => tagReturns[tag] >= 0 ? 'rgba(75, 192, 192, 0.6)' : 'rgba(255, 99, 132, 0.6)');

    return {
      labels: tags.length ? tags : ['No Tags'],
      datasets: [{
        label: 'Returns ($)',
        data: tags.length ? tags.map(tag => tagReturns[tag]) : [0],
        backgroundColor: barColors,
      }],
    };
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: { y: { title: { display: true, text: 'Total Returns ($)' } } },
  };

  const handleYearChange = (event) => setYear(event.target.value);

  const handleToggleMissedTrades = async (event) => {
    const newValue = event.target.checked;
    setShowMissedTrades(newValue);
    try {
      const prefRef = doc(db, `Users/${userID}/preferences/chartSettings`);
      const prefDoc = await getDoc(prefRef);
      const currentPrefs = prefDoc.exists() ? prefDoc.data() : {};
      await setDoc(prefRef, {
        ...currentPrefs,
        showMissedTradesInTagChart: newValue
      });
    } catch (error) {
      console.error('Error saving missed trades preference:', error);
    }
  };

  const tooltipContent = `This chart compares performance across your strategy tags, showing total returns per tag to identify your most effective strategies.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox variant="gradient" bgColor="light" color="dark" coloredShadow="light" borderRadius="xl" display="flex" justifyContent="center" alignItems="center" width="4rem" height="4rem" mt={-3}>
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Tag Performance Analysis {showMissedTrades ? '(Missed)' : ''} {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip title={tooltipContent} placement="bottom-end" componentsProps={{ tooltip: { sx: { bgcolor: theme.palette.background.paper, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}`, p: 1.5, maxWidth: '250px', fontSize: '0.75rem', lineHeight: 1.5 } } }}>
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={showMissedTrades}
                  onChange={handleToggleMissedTrades}
                  color="primary"
                  size="small"
                />
              }
              label={<MDTypography variant="caption" color="text">Missed Trades</MDTypography>}
              sx={{ m: 0 }}
            />
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Bar data={calculateTagData()} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TagPerformanceChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default TagPerformanceChart;